import { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, DollarSign, TrendingUp, Gift, Ticket, 
  BarChart3, MessageSquare, Star, ArrowLeft, 
  Plus, Trash2, ToggleLeft, ToggleRight, Copy,
  UserPlus, Activity, Calendar, ChevronDown, ChevronUp,
  Mail, Send, Pencil, Check, X, RotateCcw, FileText, Image, UtensilsCrossed
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API } from '@/App';

const RichTextEditor = lazy(() => import('@/components/RichTextEditor'));

function AdminDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [inviteCodes, setInviteCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState(null);
  const [showUsers, setShowUsers] = useState(false);
  const [showCreateCode, setShowCreateCode] = useState(false);
  const [newCode, setNewCode] = useState({ code: '', description: '', max_uses: 10 });
  const [creatingCode, setCreatingCode] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editName, setEditName] = useState('');
  const [savingUser, setSavingUser] = useState(false);
  const [resetModal, setResetModal] = useState(null);
  const [jumpWeek, setJumpWeek] = useState(1);
  const [jumpDay, setJumpDay] = useState(1);
  const [resettingUser, setResettingUser] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Articles state
  const [articles, setArticles] = useState([]);
  const [showArticles, setShowArticles] = useState(false);
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({ title: '', content: '', featured_image: '' });
  const [savingArticle, setSavingArticle] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Recipes state
  const [recipes, setRecipes] = useState([]);
  const [showRecipes, setShowRecipes] = useState(false);
  const [showRecipeForm, setShowRecipeForm] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [recipeForm, setRecipeForm] = useState({ title: '', content: '', images: [] });
  const [savingRecipe, setSavingRecipe] = useState(false);
  const [uploadingRecipeImage, setUploadingRecipeImage] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchAnalytics();
    fetchInviteCodes();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load analytics');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchInviteCodes = async () => {
    try {
      const response = await axios.get(`${API}/promo/admin/invite-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInviteCodes(response.data);
    } catch (error) {
      console.error('Failed to fetch invite codes:', error);
    }
  };

  const fetchUsers = async (filter = null) => {
    try {
      const params = filter ? `?filter_type=${filter}` : '';
      const response = await axios.get(`${API}/admin/users${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
      setUserFilter(filter);
      setShowUsers(true);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const createInviteCode = async () => {
    if (!newCode.code.trim()) {
      toast.error('Please enter a code');
      return;
    }
    setCreatingCode(true);
    try {
      await axios.post(`${API}/promo/admin/create-invite-code`, {
        code: newCode.code.toUpperCase(),
        description: newCode.description,
        max_uses: parseInt(newCode.max_uses) || -1
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invite code ${newCode.code.toUpperCase()} created!`);
      setNewCode({ code: '', description: '', max_uses: 10 });
      setShowCreateCode(false);
      fetchInviteCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create code');
    } finally {
      setCreatingCode(false);
    }
  };

  const toggleInviteCode = async (code) => {
    try {
      const response = await axios.post(`${API}/promo/admin/toggle-invite-code/${code}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Code ${code} ${response.data.is_active ? 'activated' : 'deactivated'}`);
      fetchInviteCodes();
    } catch (error) {
      toast.error('Failed to toggle code');
    }
  };

  const deleteInviteCode = async (code) => {
    if (!window.confirm(`Delete invite code ${code}?`)) return;
    try {
      await axios.delete(`${API}/promo/admin/invite-code/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Code deleted');
      fetchInviteCodes();
    } catch (error) {
      toast.error('Failed to delete code');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard!');
  };

  const sendTestEmail = async () => {
    setSendingEmail(true);
    try {
      await axios.post(`${API}/admin/send-test-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Test email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingEmail(false);
    }
  };

  const sendWeeklySummary = async () => {
    setSendingEmail(true);
    try {
      await axios.post(`${API}/admin/send-weekly-summary`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Weekly summary sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send weekly summary');
    } finally {
      setSendingEmail(false);
    }
  };

  const openResetModal = (user) => {
    setResetModal(user);
    setJumpWeek(user.current_week || 1);
    setJumpDay(user.current_day || 1);
  };

  const closeResetModal = () => {
    setResetModal(null);
    setJumpWeek(1);
    setJumpDay(1);
  };

  const resetUserProgress = async (fullReset = false) => {
    if (!resetModal) return;
    setResettingUser(true);
    try {
      const week = fullReset ? 1 : jumpWeek;
      const day = fullReset ? 1 : jumpDay;
      
      await axios.post(`${API}/admin/users/${resetModal.id}/reset-progress`, {
        week,
        day,
        full_reset: fullReset
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(fullReset 
        ? `${resetModal.display_name}'s progress reset to beginning!` 
        : `${resetModal.display_name} moved to Week ${week}, Day ${day}!`
      );
      closeResetModal();
      fetchUsers(userFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset progress');
    } finally {
      setResettingUser(false);
    }
  };

  const deleteUser = async (user) => {
    try {
      await axios.delete(`${API}/admin/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${user.display_name || user.email || 'User'} deleted`);
      setDeleteConfirm(null);
      fetchUsers(userFilter);
      fetchAnalytics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Article functions
  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles?published_only=false`);
      setArticles(response.data.articles);
    } catch (error) {
      toast.error('Failed to load articles');
    }
  };

  const handleArticleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/community/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setArticleForm(prev => ({ ...prev, featured_image: response.data.url }));
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    }
    setUploadingImage(false);
  };

  const saveArticle = async () => {
    if (!articleForm.title.trim() || !articleForm.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    
    setSavingArticle(true);
    try {
      if (editingArticle) {
        await axios.patch(`${API}/admin/articles/${editingArticle.id}`, articleForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Article updated!');
      } else {
        await axios.post(`${API}/admin/articles`, articleForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Article created!');
      }
      
      setShowArticleForm(false);
      setEditingArticle(null);
      setArticleForm({ title: '', content: '', featured_image: '' });
      fetchArticles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save article');
    }
    setSavingArticle(false);
  };

  const editArticle = (article) => {
    setEditingArticle(article);
    setArticleForm({
      title: article.title,
      content: article.content,
      featured_image: article.featured_image || ''
    });
    setShowArticleForm(true);
  };

  const deleteArticle = async (articleId) => {
    if (!confirm('Delete this article?')) return;
    
    try {
      await axios.delete(`${API}/admin/articles/${articleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Article deleted');
      fetchArticles();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  // Recipe functions
  const fetchRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes?published_only=false`);
      setRecipes(response.data.recipes);
    } catch (error) {
      toast.error('Failed to load recipes');
    }
  };

  const handleRecipeImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (recipeForm.images.length >= 3) {
      toast.error('Maximum 3 images allowed');
      return;
    }
    
    setUploadingRecipeImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/community/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setRecipeForm(prev => ({ ...prev, images: [...prev.images, response.data.url] }));
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    }
    setUploadingRecipeImage(false);
  };

  const removeRecipeImage = (index) => {
    setRecipeForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const saveRecipe = async () => {
    if (!recipeForm.title.trim() || !recipeForm.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    
    setSavingRecipe(true);
    try {
      if (editingRecipe) {
        await axios.patch(`${API}/admin/recipes/${editingRecipe.id}`, recipeForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Recipe updated!');
      } else {
        await axios.post(`${API}/admin/recipes`, recipeForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Recipe created!');
      }
      
      setShowRecipeForm(false);
      setEditingRecipe(null);
      setRecipeForm({ title: '', content: '', images: [] });
      fetchRecipes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save recipe');
    }
    setSavingRecipe(false);
  };

  const editRecipe = (recipe) => {
    setEditingRecipe(recipe);
    setRecipeForm({
      title: recipe.title,
      content: recipe.content,
      images: recipe.images || []
    });
    setShowRecipeForm(true);
  };

  const deleteRecipe = async (recipeId) => {
    if (!confirm('Delete this recipe?')) return;
    
    try {
      await axios.delete(`${API}/admin/recipes/${recipeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Recipe deleted');
      fetchRecipes();
    } catch (error) {
      toast.error('Failed to delete recipe');
    }
  };

  const startEditUser = (user) => {
    setEditingUser(user.id);
    setEditName(user.display_name || '');
  };

  const cancelEditUser = () => {
    setEditingUser(null);
    setEditName('');
  };

  const saveUserDisplayName = async (userId) => {
    if (!editName.trim()) {
      toast.error('Please enter a name');
      return;
    }
    setSavingUser(true);
    try {
      await axios.patch(`${API}/admin/users/${userId}`, {
        display_name: editName.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User name updated!');
      setEditingUser(null);
      setEditName('');
      fetchUsers(userFilter); // Refresh user list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingUser(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Failed to load dashboard</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="mb-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to App
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600">Radiant 40 Analytics & Management</p>
          </div>
          <Button onClick={fetchAnalytics} variant="outline">
            Refresh Data
          </Button>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <MetricCard
            icon={<Users className="w-6 h-6" />}
            label="Total Users"
            value={analytics.users.total}
            color="blue"
            onClick={() => fetchUsers(null)}
          />
          <MetricCard
            icon={<DollarSign className="w-6 h-6" />}
            label="Revenue"
            value={`$${analytics.revenue.estimated_total.toLocaleString()}`}
            color="green"
          />
          <MetricCard
            icon={<Gift className="w-6 h-6" />}
            label="Founding Members"
            value={`${analytics.promo_status.founding_members_claimed}/50`}
            subtext={`${analytics.promo_status.founding_members_remaining} spots left`}
            color="amber"
            onClick={() => fetchUsers('founding')}
          />
          <MetricCard
            icon={<TrendingUp className="w-6 h-6" />}
            label="Active Subs"
            value={analytics.users.active_subscriptions}
            color="purple"
            onClick={() => fetchUsers('active')}
          />
        </div>

        {/* Signup Stats */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <UserPlus className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Signups</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Today</span>
                <span className="font-semibold">{analytics.signups.today}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">This Week</span>
                <span className="font-semibold">{analytics.signups.this_week}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">This Month</span>
                <span className="font-semibold">{analytics.signups.this_month}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Subscription Types</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Paid Users</span>
                <span className="font-semibold text-green-600 cursor-pointer hover:underline" onClick={() => fetchUsers('paid')}>
                  {analytics.users.paid}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Monthly</span>
                <span className="font-semibold">{analytics.subscriptions.monthly}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Full Course</span>
                <span className="font-semibold">{analytics.subscriptions.full_course}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Activity className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Engagement</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Task Completions</span>
                <span className="font-semibold">{analytics.engagement.total_task_completions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">This Week</span>
                <span className="font-semibold">{analytics.engagement.completions_this_week}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Community Posts</span>
                <span className="font-semibold">{analytics.engagement.total_community_posts}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Email Notifications Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-rose-100 rounded-lg">
                <Mail className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Email Notifications</h3>
                <p className="text-sm text-gray-500">
                  Receive alerts for signups, testimonials & payments
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={sendTestEmail} 
                disabled={sendingEmail}
                variant="outline"
                size="sm"
              >
                <Mail className="w-4 h-4 mr-1" />
                {sendingEmail ? 'Sending...' : 'Test Email'}
              </Button>
              <Button 
                onClick={sendWeeklySummary} 
                disabled={sendingEmail}
                size="sm"
                className="bg-rose-500 hover:bg-rose-600"
              >
                <Send className="w-4 h-4 mr-1" />
                {sendingEmail ? 'Sending...' : 'Send Summary'}
              </Button>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-600">
              <strong>You'll be notified when:</strong>
            </p>
            <ul className="text-sm text-gray-500 mt-2 space-y-1">
              <li>• A new founding member signs up</li>
              <li>• Someone uses an invite code</li>
              <li>• A testimonial is submitted for review</li>
              <li>• You receive a payment</li>
            </ul>
          </div>
        </div>

        {/* Invite Codes Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Ticket className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Invite Codes</h3>
                <p className="text-sm text-gray-500">
                  {analytics.invite_codes.active_codes} active • {analytics.invite_codes.total_uses} total uses
                </p>
              </div>
            </div>
            <Button onClick={() => setShowCreateCode(!showCreateCode)} size="sm">
              <Plus className="w-4 h-4 mr-1" /> Create Code
            </Button>
          </div>

          {/* Create Code Form */}
          {showCreateCode && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm">Code</Label>
                  <Input
                    value={newCode.code}
                    onChange={(e) => setNewCode({...newCode, code: e.target.value.toUpperCase()})}
                    placeholder="FRIEND50"
                    className="font-mono uppercase"
                  />
                </div>
                <div>
                  <Label className="text-sm">Description</Label>
                  <Input
                    value={newCode.description}
                    onChange={(e) => setNewCode({...newCode, description: e.target.value})}
                    placeholder="For friends & family"
                  />
                </div>
                <div>
                  <Label className="text-sm">Max Uses (-1 = unlimited)</Label>
                  <Input
                    type="number"
                    value={newCode.max_uses}
                    onChange={(e) => setNewCode({...newCode, max_uses: e.target.value})}
                    placeholder="10"
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={createInviteCode} disabled={creatingCode} className="w-full">
                    {creatingCode ? 'Creating...' : 'Create'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Codes List */}
          <div className="space-y-3">
            {inviteCodes.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No invite codes yet</p>
            ) : (
              inviteCodes.map((code) => (
                <div key={code.code} className={`flex items-center justify-between p-3 rounded-lg ${code.is_active ? 'bg-green-50' : 'bg-gray-100'}`}>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => copyCode(code.code)}
                      className="font-mono text-lg font-bold text-gray-900 hover:text-primary flex items-center gap-1"
                    >
                      {code.code}
                      <Copy className="w-4 h-4 text-gray-400" />
                    </button>
                    <span className="text-sm text-gray-500">{code.description || 'No description'}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600">
                      {code.times_used}/{code.max_uses === -1 ? '∞' : code.max_uses} uses
                    </span>
                    <button
                      onClick={() => toggleInviteCode(code.code)}
                      className={`p-1 rounded ${code.is_active ? 'text-green-600' : 'text-gray-400'}`}
                    >
                      {code.is_active ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
                    </button>
                    <button
                      onClick={() => deleteInviteCode(code.code)}
                      className="p-1 text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Testimonials & Moderation */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Star className="w-5 h-5 text-yellow-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Testimonials</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Pending Review</span>
                <span className={`font-semibold ${analytics.testimonials.pending > 0 ? 'text-amber-600' : ''}`}>
                  {analytics.testimonials.pending}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Approved</span>
                <span className="font-semibold text-green-600">{analytics.testimonials.approved}</span>
              </div>
            </div>
            {analytics.testimonials.pending > 0 && (
              <Button 
                onClick={() => navigate('/admin/testimonials')} 
                variant="outline" 
                className="w-full mt-4"
              >
                Review Testimonials
              </Button>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-rose-100 rounded-lg">
                <BarChart3 className="w-5 h-5 text-rose-600" />
              </div>
              <h3 className="font-semibold text-gray-900">User Progress Distribution</h3>
            </div>
            <div className="space-y-2">
              {Object.entries(analytics.progress_distribution).slice(0, 6).map(([week, count]) => (
                <div key={week} className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 w-16">{week.replace('_', ' ')}</span>
                  <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-primary to-rose-400"
                      style={{ width: `${(count / analytics.users.total) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium w-8">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Users List (Expandable) */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <button 
            onClick={() => {
              const newState = !showUsers;
              setShowUsers(newState);
              // Auto-fetch users when expanding for the first time
              if (newState && users.length === 0) {
                fetchUsers(null);
              }
            }}
            className="flex items-center justify-between w-full"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900">User List</h3>
            </div>
            {showUsers ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {showUsers && (
            <div className="mt-4">
              <div className="flex gap-2 mb-4 flex-wrap">
                <Button size="sm" variant={userFilter === null ? 'default' : 'outline'} onClick={() => fetchUsers(null)}>All</Button>
                <Button size="sm" variant={userFilter === 'paid' ? 'default' : 'outline'} onClick={() => fetchUsers('paid')}>Paid</Button>
                <Button size="sm" variant={userFilter === 'founding' ? 'default' : 'outline'} onClick={() => fetchUsers('founding')}>Founding</Button>
                <Button size="sm" variant={userFilter === 'invite' ? 'default' : 'outline'} onClick={() => fetchUsers('invite')}>Invite Code</Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3">Name</th>
                      <th className="text-left p-3">Email</th>
                      <th className="text-left p-3">Type</th>
                      <th className="text-left p-3">Progress</th>
                      <th className="text-left p-3">Joined</th>
                      <th className="text-left p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="p-3">
                          {editingUser === user.id ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                className="h-8 w-32"
                                placeholder="Enter name"
                                autoFocus
                              />
                              <button
                                onClick={() => saveUserDisplayName(user.id)}
                                disabled={savingUser}
                                className="p-1 text-green-600 hover:text-green-800"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={cancelEditUser}
                                className="p-1 text-gray-500 hover:text-gray-700"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <span className={!user.display_name ? 'text-red-500 italic' : ''}>
                              {user.display_name || '(No name)'}
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-gray-600">{user.email}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.signup_type === 'founding_member' ? 'bg-amber-100 text-amber-700' :
                            user.signup_type === 'invite_code' ? 'bg-purple-100 text-purple-700' :
                            user.subscription_source === 'stripe' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {user.signup_type === 'founding_member' ? 'Founding' :
                             user.signup_type === 'invite_code' ? 'Invite' :
                             user.subscription_source === 'stripe' ? 'Paid' : 'Free'}
                          </span>
                        </td>
                        <td className="p-3">Week {user.current_week}, Day {user.current_day}</td>
                        <td className="p-3 text-gray-500">{new Date(user.created_at).toLocaleDateString()}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            {editingUser !== user.id && (
                              <button
                                onClick={() => startEditUser(user)}
                                className="p-1 text-blue-600 hover:text-blue-800"
                                title="Edit user name"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => openResetModal(user)}
                              className="p-1 text-orange-600 hover:text-orange-800"
                              title="Reset/Jump to day"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(user)}
                              className="p-1 text-red-600 hover:text-red-800"
                              title="Delete user"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Recent Signups */}
        {analytics.signups.recent.length > 0 && (
          <div className="mt-6 bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Recent Signups</h3>
            <div className="space-y-2">
              {analytics.signups.recent.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                  <div>
                    <span className="font-medium">{user.display_name}</span>
                    <span className="text-gray-500 ml-2">{user.email}</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {new Date(user.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Articles Management */}
        <div className="mt-6 bg-white rounded-xl shadow-sm p-6">
          <button 
            onClick={() => {
              setShowArticles(!showArticles);
              if (!showArticles && articles.length === 0) fetchArticles();
            }}
            className="flex items-center justify-between w-full"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <FileText className="w-5 h-5 text-indigo-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Articles</h3>
            </div>
            {showArticles ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {showArticles && (
            <div className="mt-4">
              <Button
                onClick={() => {
                  setEditingArticle(null);
                  setArticleForm({ title: '', content: '', featured_image: '' });
                  setShowArticleForm(true);
                }}
                className="mb-4"
              >
                <Plus className="w-4 h-4 mr-2" /> New Article
              </Button>

              {showArticleForm && (
                <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                  <h4 className="font-medium mb-4">{editingArticle ? 'Edit Article' : 'New Article'}</h4>
                  <div className="space-y-4">
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={articleForm.title}
                        onChange={(e) => setArticleForm(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Article title"
                      />
                    </div>
                    <div>
                      <Label>Content</Label>
                      <p className="text-xs text-gray-500 mb-2">Paste from Word or Google Docs - formatting will be preserved.</p>
                      <Suspense fallback={<div className="border rounded-lg p-4 min-h-[300px] bg-white">Loading editor...</div>}>
                        <RichTextEditor
                          value={articleForm.content}
                          onChange={(value) => setArticleForm(prev => ({ ...prev, content: value }))}
                        />
                      </Suspense>
                    </div>
                    <div>
                      <Label>Featured Image</Label>
                      <div className="flex items-center gap-4 mt-2">
                        {articleForm.featured_image && (
                          <img
                            src={articleForm.featured_image}
                            alt="Preview"
                            className="w-24 h-24 object-cover rounded-lg"
                          />
                        )}
                        <label className="cursor-pointer">
                          <div className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-100">
                            <Image className="w-4 h-4" />
                            {uploadingImage ? 'Uploading...' : 'Upload Image'}
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleArticleImageUpload}
                            className="hidden"
                            disabled={uploadingImage}
                          />
                        </label>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={saveArticle} disabled={savingArticle}>
                        {savingArticle ? 'Saving...' : 'Save Article'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowArticleForm(false);
                          setEditingArticle(null);
                          setArticleForm({ title: '', content: '', featured_image: '' });
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {articles.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No articles yet</p>
              ) : (
                <div className="space-y-3">
                  {articles.map((article) => (
                    <div key={article.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        {article.featured_image && (
                          <img
                            src={article.featured_image}
                            alt=""
                            className="w-12 h-12 object-cover rounded"
                          />
                        )}
                        <div>
                          <h4 className="font-medium">{article.title}</h4>
                          <p className="text-sm text-gray-500">
                            {new Date(article.created_at).toLocaleDateString()}
                            {!article.published && <span className="ml-2 text-amber-600">(Draft)</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`https://radiant40.com/api/share/${article.slug}`);
                            toast.success('Share link copied!');
                          }}
                          className="p-2 text-green-600 hover:text-green-800"
                          title="Copy share link for social media"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => editArticle(article)}
                          className="p-2 text-blue-600 hover:text-blue-800"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteArticle(article.id)}
                          className="p-2 text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Recipes Management */}
        <div className="mt-6 bg-white rounded-xl shadow-sm p-6">
          <button 
            onClick={() => {
              setShowRecipes(!showRecipes);
              if (!showRecipes && recipes.length === 0) fetchRecipes();
            }}
            className="flex items-center justify-between w-full"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <UtensilsCrossed className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Healthy Recipes</h3>
            </div>
            {showRecipes ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {showRecipes && (
            <div className="mt-4">
              <Button
                onClick={() => {
                  setEditingRecipe(null);
                  setRecipeForm({ title: '', content: '', images: [] });
                  setShowRecipeForm(true);
                }}
                className="mb-4"
              >
                <Plus className="w-4 h-4 mr-2" /> New Recipe
              </Button>

              {showRecipeForm && (
                <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                  <h4 className="font-medium mb-4">{editingRecipe ? 'Edit Recipe' : 'New Recipe'}</h4>
                  <div className="space-y-4">
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={recipeForm.title}
                        onChange={(e) => setRecipeForm(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Recipe title"
                      />
                    </div>
                    <div>
                      <Label>Content</Label>
                      <p className="text-xs text-gray-500 mb-2">Paste from Word or Google Docs - formatting will be preserved.</p>
                      <Suspense fallback={<div className="border rounded-lg p-4 min-h-[300px] bg-white">Loading editor...</div>}>
                        <RichTextEditor
                          value={recipeForm.content}
                          onChange={(value) => setRecipeForm(prev => ({ ...prev, content: value }))}
                        />
                      </Suspense>
                    </div>
                    <div>
                      <Label>Images (up to 3)</Label>
                      <div className="flex items-center gap-4 mt-2 flex-wrap">
                        {recipeForm.images.map((img, index) => (
                          <div key={index} className="relative">
                            <img
                              src={img}
                              alt={`Preview ${index + 1}`}
                              className="w-24 h-24 object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => removeRecipeImage(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        {recipeForm.images.length < 3 && (
                          <label className="cursor-pointer">
                            <div className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-100">
                              <Image className="w-4 h-4" />
                              {uploadingRecipeImage ? 'Uploading...' : 'Add Image'}
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleRecipeImageUpload}
                              className="hidden"
                              disabled={uploadingRecipeImage}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={saveRecipe} disabled={savingRecipe}>
                        {savingRecipe ? 'Saving...' : 'Save Recipe'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowRecipeForm(false);
                          setEditingRecipe(null);
                          setRecipeForm({ title: '', content: '', images: [] });
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {recipes.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No recipes yet</p>
              ) : (
                <div className="space-y-3">
                  {recipes.map((recipe) => (
                    <div key={recipe.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        {recipe.images && recipe.images.length > 0 && (
                          <img
                            src={recipe.images[0]}
                            alt=""
                            className="w-12 h-12 object-cover rounded"
                          />
                        )}
                        <div>
                          <h4 className="font-medium">{recipe.title}</h4>
                          <p className="text-sm text-gray-500">
                            {new Date(recipe.created_at).toLocaleDateString()}
                            {recipe.images && recipe.images.length > 0 && (
                              <span className="ml-2 text-green-600">({recipe.images.length} images)</span>
                            )}
                            {!recipe.published && <span className="ml-2 text-amber-600">(Draft)</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`https://radiant40.com/api/share/recipe/${recipe.slug}`);
                            toast.success('Share link copied!');
                          }}
                          className="p-2 text-green-600 hover:text-green-800"
                          title="Copy share link for social media"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => editRecipe(recipe)}
                          className="p-2 text-blue-600 hover:text-blue-800"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteRecipe(recipe.id)}
                          className="p-2 text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reset Progress Modal */}
      {resetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              Reset Progress: {resetModal.display_name}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Current: Week {resetModal.current_week}, Day {resetModal.current_day}
            </p>
            
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Jump to specific day:</Label>
                <div className="flex gap-2 mt-2">
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500">Week</Label>
                    <select
                      value={jumpWeek}
                      onChange={(e) => setJumpWeek(Number(e.target.value))}
                      className="w-full border rounded-lg p-2"
                    >
                      {[...Array(12)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>Week {i + 1}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500">Day</Label>
                    <select
                      value={jumpDay}
                      onChange={(e) => setJumpDay(Number(e.target.value))}
                      className="w-full border rounded-lg p-2"
                    >
                      {[...Array(7)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>Day {i + 1}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={() => resetUserProgress(false)}
                  disabled={resettingUser}
                  className="flex-1"
                >
                  Jump to Day
                </Button>
                <Button
                  onClick={() => resetUserProgress(true)}
                  disabled={resettingUser}
                  variant="destructive"
                  className="flex-1"
                >
                  Full Reset
                </Button>
              </div>
              
              <Button
                onClick={closeResetModal}
                variant="outline"
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-2 text-red-600">Delete User?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete <strong>{deleteConfirm.display_name || deleteConfirm.email}</strong>? This cannot be undone.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={() => deleteUser(deleteConfirm)}
                variant="destructive"
                className="flex-1"
              >
                Delete
              </Button>
              <Button
                onClick={() => setDeleteConfirm(null)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ icon, label, value, subtext, color, onClick }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    amber: 'bg-amber-100 text-amber-600',
    purple: 'bg-purple-100 text-purple-600',
    rose: 'bg-rose-100 text-rose-600'
  };

  return (
    <div 
      className={`bg-white rounded-xl shadow-sm p-6 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className={`inline-flex p-2 rounded-lg ${colorClasses[color]} mb-3`}>
        {icon}
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-sm text-gray-600">{label}</div>
      {subtext && <div className="text-xs text-gray-500 mt-1">{subtext}</div>}
    </div>
  );
}

export default AdminDashboardPage;
